import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AOS from 'aos';
import { FaCheckCircle, FaAward, FaClock, FaShieldAlt, FaUsers, FaTools, FaChartLine, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';
import { seoContent } from '../config/seoContent';
import { servicesData } from '../data/servicesData';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find(s => s.id === serviceId);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return <div>Service not found</div>;
  }

  const seoKey = serviceId.replace(/-/g, '-');
  const seoData = seoContent[seoKey] || seoContent[serviceId.replace('-', '')] || seoContent.services;

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
      />

      <div className="page-header service-detail-header">
        <div className="container">
          <h1 data-aos="fade-up">{service.title}</h1>
          <p data-aos="fade-up" data-aos-delay="100">{service.shortDesc}</p>
        </div>
      </div>

      <section className="service-detail-section">
        <div className="container">
          {/* Hero Section - Image Left, About Right */}
          <div className="service-hero-grid" data-aos="fade-up">
            <div className="hero-image-side">
              <img src={service.image} alt={service.title} />
            </div>
            <div className="hero-content-side">
              <div className="service-icon-badge">{service.icon}</div>
              <h2>About This Service</h2>
              <p>{service.fullDescription}</p>
              <div className="hero-stats">
                <div className="stat-item">
                  <FaAward />
                  <span>Quality Assured</span>
                </div>
                <div className="stat-item">
                  <FaClock />
                  <span>On-Time Delivery</span>
                </div>
                <div className="stat-item">
                  <FaShieldAlt />
                  <span>Safety First</span>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose This Service */}
          <div className="why-choose-section" data-aos="fade-up">
            <h2>Why Choose This Service</h2>
            <div className="why-choose-grid">
              <div className="why-card">
                <FaAward />
                <h3>Quality Assured</h3>
                <p>Highest standards of quality in every project we undertake</p>
              </div>
              <div className="why-card">
                <FaClock />
                <h3>On-Time Delivery</h3>
                <p>We respect deadlines and complete projects on schedule</p>
              </div>
              <div className="why-card">
                <FaShieldAlt />
                <h3>Safety First</h3>
                <p>Comprehensive safety measures for all operations</p>
              </div>
              <div className="why-card">
                <FaUsers />
                <h3>Expert Team</h3>
                <p>Skilled professionals with years of experience</p>
              </div>
            </div>
          </div>

          {/* Features & Benefits Grid */}
          <div className="features-benefits-grid" data-aos="fade-up">
            <div className="sd-features-section">
              <h2><FaTools /> Key Features</h2>
              <ul className="feature-list">
                {service.features.map((feature, idx) => (
                  <li key={idx}><FaCheckCircle /> {feature}</li>
                ))}
              </ul>
            </div>

            <div className="benefits-section">
              <h2><FaChartLine /> Benefits</h2>
              <ul className="feature-list">
                {service.benefits.map((benefit, idx) => (
                  <li key={idx}><FaCheckCircle /> {benefit}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Process Section - New Design */}
          <div className="process-section-new" data-aos="fade-up">
            <h2>Our Process</h2>
            <p className="process-subtitle">Step-by-step approach to ensure excellence</p>
            <div className="process-grid">
              {service.process.map((step, idx) => (
                <div key={idx} className="process-card">
                  <div className="process-card-number">{idx + 1}</div>
                  <div className="process-card-content">
                    <h4>Step {idx + 1}</h4>
                    <p>{step}</p>
                  </div>
                  <div className="process-card-arrow">→</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="service-cta" data-aos="zoom-in">
            <div className="cta-content">
              <h2>Ready to Get Started?</h2>
              <p>Contact us today for a free consultation and detailed quote for your project</p>
              <div className="cta-buttons">
                <a href="tel:9894065756" className="btn btn-primary">
                  <FaPhoneAlt /> Call: 9894065756
                </a>
                <Link to="/contact" className="btn btn-secondary">
                  <FaEnvelope /> Contact Form
                </Link>
              </div>
            </div>
          </div>

          {/* Other Services */}
          <div className="other-services-section" data-aos="fade-up">
            <h2>Explore Our Other Services</h2>
            <div className="other-services-grid">
              {servicesData.filter(s => s.id !== serviceId).map(s => (
                <Link key={s.id} to={`/services/${s.id}`} className="other-service-card">
                  <div className="other-service-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.shortDesc}</p>
                  <span className="view-more">View Details →</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
